import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import type { Table, Column, Relation, RelationType, Groups, Group, DatabaseDialect, ColumnDefaults } from '@/types'
import {
  normTable,
  normCol,
  normRel,
  normGroup,
  normGroups,
  buildFullTableName,
  INIT_DEFAULTS,
} from '@/utils/normalizers'
import { isForeignKey, isPrimaryKey } from '@/utils/columnKeys'
import { genId } from '@/utils/id'
import { newUid } from '@/utils/uid'
import { migrateDiagramIdentity } from '@/services/identityMigration'
import { TW, getTableHeight, getTableWidth } from '@/utils/geometry'
import { calculateImportedTablesLayout } from '@/services/incrementalImportService'
import { useHistoryStore } from './historyStore'
import type { DiagramSnapshot } from './historyStore'
import { diagramStorage } from './persistedStorage'
import { useTabViewStore } from './tabViewStore'
import { getRelationshipCardinalities, relationTypeFromCardinalities } from '@/utils/relationships'
import { pinAutomaticRelationshipRoutes, pinRelationshipRoute } from '@/utils/paths'
import { isValidSqlIdentifier } from '@/utils/sqlIdentifiers'

// ── Pure helpers (not exposed in store state) ─────────────────────────────────

// Ensures at most one PK per table; extra PKs become plain columns.
function enforceSinglePK(cols: Column[]): Column[] {
  let pkSeen = false
  return cols.map((col) => {
    if (!isPrimaryKey(col)) return col
    if (!pkSeen) {
      pkSeen = true
      return normCol({ ...col, pk: true, uq: true, fk: false })
    }
    return normCol({ ...col, pk: false })
  })
}

/** Converte tipos geradores de sequencia para o tipo armazenado pela FK. */
export function getForeignKeyType(referencedTable: Pick<Table, 'cols'>): string {
  const primaryKey = referencedTable.cols.find((column) => isPrimaryKey(column))
  const referencedType = primaryKey?.type.trim() || 'BIGINT'
  const normalized = referencedType.toUpperCase().replace(/\s+/g, ' ')

  if (normalized === 'SERIAL' || normalized === 'SERIAL4') return 'INTEGER'
  if (normalized === 'SMALLSERIAL' || normalized === 'SERIAL2') return 'SMALLINT'
  if (normalized === 'BIGSERIAL' || normalized === 'SERIAL8') return 'BIGINT'

  return referencedType
    .replace(/\s+AUTO_INCREMENT\b/gi, '')
    .replace(/\s+IDENTITY(?:\s*\([^)]*\))?/gi, '')
    .trim() || 'BIGINT'
}

function createFkColumn(input: { name: string; refId: string; nullable: boolean; type: string }): Column {
  return normCol({
    key: 'FK',
    name: input.name,
    type: input.type,
    notNull: !input.nullable,
    blank: input.nullable,
    desc: `FK to ${input.refId}`,
  })
}

// Inserts a FK column before the first suffix column (or appends if none).
// Returns a new array; does nothing if colName already exists.
function insertFKCol(
  cols: Column[],
  colName: string,
  refId: string,
  nullable: boolean,
  suffixNames: Set<string>,
  type: string,
): Column[] {
  if (cols.find((c) => c.name === colName)) return cols

  const fkCol = createFkColumn({ name: colName, refId, nullable, type })

  const insertAt = cols.findIndex((c) => suffixNames.has(c.name))
  if (insertAt >= 0) {
    return [...cols.slice(0, insertAt), fkCol, ...cols.slice(insertAt)]
  }
  return [...cols, fkCol]
}

function buildNextFkColumnName(
  existingCols: Column[],
  baseName: string,
): string {
  const takenNames = new Set(existingCols.map((column) => column.name))
  if (!takenNames.has(baseName)) return baseName

  let counter = 2
  while (takenNames.has(`${baseName}_${counter}`)) {
    counter += 1
  }
  return `${baseName}_${counter}`
}

function buildParallelRelationshipMid(
  rels: Relation[],
  tableA: string,
  tableB: string,
): number | null {
  const parallelCount = rels.filter(
    (rel) =>
      (rel.from === tableA && rel.to === tableB) ||
      (rel.from === tableB && rel.to === tableA),
  ).length

  if (parallelCount === 0) return null

  const step = Math.floor((parallelCount + 1) / 2)
  const magnitude = step * 48
  return parallelCount % 2 === 1 ? magnitude : -magnitude
}

// Returns true when any relationship OTHER than ignoreRelId uses that FK col.
function fkColStillUsed(
  rels: Relation[],
  tblId: string,
  colName: string,
  ignoreRelId: string,
): boolean {
  return rels.some(
    (r) => r.id !== ignoreRelId && r.from === tblId && r.col === colName,
  )
}

function removeTablesBatch(
  tables: Table[],
  rels: Relation[],
  idsToDelete: string[],
): { tables: Table[]; rels: Relation[] } {
  const deletedIds = new Set(idsToDelete)
  if (deletedIds.size === 0) {
    return { tables, rels }
  }

  const connected = rels.filter((rel) => deletedIds.has(rel.from) || deletedIds.has(rel.to))
  const fkToRemove = new Map<string, Set<string>>()

  connected.forEach((rel) => {
    if (deletedIds.has(rel.from)) return
    if (!fkToRemove.has(rel.from)) fkToRemove.set(rel.from, new Set())
    fkToRemove.get(rel.from)!.add(rel.col)
  })

  const nextTables = tables
    .filter((table) => !deletedIds.has(table.id))
    .map((table) => {
      const cols = fkToRemove.get(table.id)
      if (!cols) return table
      return { ...table, cols: table.cols.filter((column) => !cols.has(column.name)) }
    })

  return {
    tables: nextTables,
    rels: rels.filter((rel) => !deletedIds.has(rel.from) && !deletedIds.has(rel.to)),
  }
}

// ── History helper ────────────────────────────────────────────────────────────

/**
 * Registra o estado atual do diagramStore no histórico.
 * Deve ser chamado APÓS o set() retornar (estado já aplicado).
 * `state` é o estado pós-mutação lido via get() dentro da action.
 *
 * Não é chamado por: loadDiagram, setTables, setRelations, setGroups
 * (operações de carga em bloco — o chamador decide se registra histórico).
 */
function recordHistory(state: Pick<DiagramState, 'tables' | 'rels' | 'groups' | 'defaults' | 'projectName' | 'dialect'>, label: string): void {
  const snapshot: DiagramSnapshot = {
    tables: state.tables,
    rels: state.rels,
    groups: state.groups,
    defaults: state.defaults,
    projectName: state.projectName,
    dialect: state.dialect,
    hiddenTableIds: useTabViewStore.getState().hiddenTableIds,
  }
  useHistoryStore.getState().commitHistory(snapshot, label)
}

function buildUniqueTableCopyIdentity(
  existingIds: Set<string>,
  name: string,
  schema = '',
): { id: string; name: string; schema: string; fullName: string } {
  const trimmedSchema = String(schema || '').trim()
  const baseName = String(name || '').trim() || 'table'
  let counter = 0

  while (true) {
    const nextName = counter === 0 ? `${baseName}_copy` : `${baseName}_copy_${counter}`
    const fullName = buildFullTableName(trimmedSchema, nextName)
    if (fullName && !existingIds.has(fullName)) {
      return {
        id: fullName,
        name: nextName,
        schema: trimmedSchema,
        fullName,
      }
    }
    counter += 1
  }
}

// ── Store interface ───────────────────────────────────────────────────────────

interface DiagramState {
  tables: Table[]
  rels: Relation[]
  groups: Groups
  defaults: ColumnDefaults
  projectName: string
  dialect: DatabaseDialect

  // ── Table CRUD ─────────────────────────────────────────────────────────────
  addTable: (table: Partial<Table>) => void
  saveTable: (input: {
    originalId?: string | null
    table: Partial<Table>
    renamedFkColumns?: Array<{
      previousName: string
      nextName: string
    }>
  }) => Table | null
  updateTable: (id: string, changes: Partial<Table>) => void
  deleteTable: (id: string) => void
  deleteTables: (ids: string[]) => void
  duplicateTable: (id: string) => void
  /**
   * Renames a table, updating its id/fullName and all rel references.
   * Fixes bug F-69: the legacy ternary `tbl.id === fullName ? tbl.id : editingTblId`
   * was always equivalent to `editingTblId`; here we use the old id directly.
   * FK column *names* in other tables (e.g. `id_users`) are intentionally kept
   * unchanged — they match legacy behaviour and remain consistent via rel.col.
   */
  renameTable: (id: string, name: string, schema?: string) => void

  // ── Relation CRUD ──────────────────────────────────────────────────────────
  /**
   * Creates a relationship with automatic FK management:
   * - 1-1, 0-1, 1-N, 0-N: adds FK to the "N/many" side (tgtId).
   * - N-N: creates junction table with two FKs using current defaults.
   * - Self relationships are allowed.
   */
  createRelationship: (srcId: string, tgtId: string, type: RelationType) => void
  deleteRelationship: (relId: string) => void
  updateRelationship: (relId: string, changes: Partial<Relation>) => void
  invertRelationship: (relId: string) => Relation | null

  // ── Group CRUD ─────────────────────────────────────────────────────────────
  addGroup: (group: Partial<Group>) => void
  updateGroup: (id: string, changes: Partial<Group>) => void
  /** Deletes group and moves its tables to no-group (""). */
  deleteGroup: (id: string) => void

  // ── Bulk / compat helpers ──────────────────────────────────────────────────
  /** Adds multiple tables and relationships atomically; commits a single undo entry. */
  pasteTablesAndRels: (tables: Table[], rels: Relation[]) => void
  /** Reorganiza todas as tabelas usando layout radial BFS por relacionamento. */
  autoLayout: () => void
  setTables: (tables: Table[]) => void
  setRelations: (rels: Relation[]) => void
  setGroups: (groups: Groups) => void
  setDefaults: (defaults: ColumnDefaults) => void
  setProjectName: (name: string) => void
  setDialect: (dialect: DatabaseDialect) => void
  loadDiagram: (data: {
    tables: Table[]
    rels: Relation[]
    groups: Groups
    defaults?: ColumnDefaults
    projectName?: string
    dialect?: DatabaseDialect
  }) => void
}

// ── Store implementation ──────────────────────────────────────────────────────

export const useDiagramStore = create<DiagramState>()(
  persist(
    (set, get) => ({
      tables: [],
      rels: [],
      groups: {},
      defaults: INIT_DEFAULTS,
      projectName: 'MorphERD',
      dialect: 'postgresql',

      // ────────────────────────────────────────────────────────────────────────
      // TABLE CRUD
      // ────────────────────────────────────────────────────────────────────────

      addTable: (table) => {
        set((s) => {
          const norm = normTable(table) // norm.id = UUID
          if (
            !isValidSqlIdentifier(norm.name) ||
            (norm.schema !== '' && !isValidSqlIdentifier(norm.schema)) ||
            norm.cols.some((column) => !isValidSqlIdentifier(column.name))
          ) {
            return s
          }
          // Enforce unique SQL name (fullName), NOT id.
          let name = norm.name
          let fullName = buildFullTableName(norm.schema, name) || name
          let counter = 1
          while (s.tables.some((t) => t.fullName === fullName)) {
            name = `${norm.name}_${counter++}`
            fullName = buildFullTableName(norm.schema, name) || name
          }
          const final: Table = {
            ...norm,
            name,
            fullName,
            cols: enforceSinglePK(norm.cols),
          }
          return { tables: [...s.tables, final] }
        })
        recordHistory(get(), 'add-table')
      },

      saveTable: ({ originalId, table, renamedFkColumns = [] }) => {
        let savedTable: Table | null = null

        set((s) => {
          const name = String(table.name || '').trim()
          const schema = String(table.schema || '').trim()
          const fullName = buildFullTableName(schema, name)
          const nextCols = enforceSinglePK((table.cols ?? []).map((col) => normCol(col)))

          if (
            !fullName ||
            !isValidSqlIdentifier(name) ||
            (schema !== '' && !isValidSqlIdentifier(schema)) ||
            nextCols.some((column) => !isValidSqlIdentifier(column.name))
          ) return s

          if (originalId) {
            const idx = s.tables.findIndex((entry) => entry.id === originalId)
            if (idx < 0) return s

            const existing = s.tables[idx]
            const oldFKNames = new Set(
              existing.cols.filter((col) => isForeignKey(col)).map((col) => col.name),
            )
            const nextFKNames = new Set(
              nextCols.filter((col) => isForeignKey(col)).map((col) => col.name),
            )
            const renamedFkMap = new Map(
              renamedFkColumns
                .map(({ previousName, nextName }) => [String(previousName || '').trim(), String(nextName || '').trim()] as const)
                .filter(([previousName, nextName]) =>
                  previousName &&
                  nextName &&
                  previousName !== nextName &&
                  oldFKNames.has(previousName) &&
                  nextFKNames.has(nextName),
                ),
            )

            // id da tabela é UUID e NÃO muda no rename → rels (from/to) intactos.
            // Só atualizamos campos denormalizados de exibição e o NOME da FK col.
            let nextRels = s.rels.map((rel) => {
              const nextColumnName = rel.from === originalId && renamedFkMap.has(rel.col)
                ? renamedFkMap.get(rel.col) || rel.col
                : rel.col
              const nextFkColumn = rel.from === originalId
                ? nextCols.find((column) => column.name === nextColumnName)
                : undefined
              const currentCardinalities = getRelationshipCardinalities(rel)
              const toCardinality = nextFkColumn
                ? (nextFkColumn.notNull ? 'one' : 'zero-one')
                : currentCardinalities.to

              return {
                ...rel,
                fromTableName: rel.from === originalId ? fullName : rel.fromTableName,
                toTableName: rel.to === originalId ? fullName : rel.toTableName,
                fromSchema: rel.from === originalId ? schema : rel.fromSchema,
                toSchema: rel.to === originalId ? schema : rel.toSchema,
                col: nextColumnName,
                fromColumn:
                  rel.from === originalId && renamedFkMap.has(rel.fromColumn)
                    ? renamedFkMap.get(rel.fromColumn) || rel.fromColumn
                    : rel.fromColumn,
                ...(nextFkColumn ? {
                  toCardinality,
                  type: relationTypeFromCardinalities(currentCardinalities.from, toCardinality),
                } : {}),
              }
            })

            oldFKNames.forEach((colName) => {
              if (!nextFKNames.has(colName) && !renamedFkMap.has(colName)) {
                nextRels = nextRels.filter(
                  (rel) => !(rel.from === originalId && rel.col === colName),
                )
              }
            })

            const updated: Table = normTable({
              ...existing,
              ...table,
              id: existing.id,
              fullName,
              name,
              schema,
              cols: nextCols,
              indexes: table.indexes ?? existing.indexes,
            })

            savedTable = updated

            const nextTables = [...s.tables]
            nextTables[idx] = updated
            return { tables: nextTables, rels: nextRels }
          }

          if (s.tables.some((entry) => entry.fullName === fullName)) {
            return s
          }

          const created = normTable({
            ...table,
            id: newUid(),
            fullName,
            name,
            schema,
            cols: nextCols,
          })

          savedTable = created
          return { tables: [...s.tables, created] }
        })

        if (savedTable) {
          recordHistory(get(), originalId ? 'save-table:edit' : 'save-table:create')
        }

        return savedTable
      },

      updateTable: (id, changes) => {
        set((s) => {
          const idx = s.tables.findIndex((t) => t.id === id)
          if (idx < 0) return s

          const existing = s.tables[idx]

          // Normalise and enforce PK on changed columns
          let newCols: Column[] | undefined
          if (changes.cols !== undefined) {
            newCols = enforceSinglePK(changes.cols.map((c) => normCol(c)))
            if (newCols.some((column) => !isValidSqlIdentifier(column.name))) return s
          }

          if (changes.name !== undefined && !isValidSqlIdentifier(changes.name.trim())) return s
          if (
            changes.schema !== undefined &&
            changes.schema.trim() !== '' &&
            !isValidSqlIdentifier(changes.schema.trim())
          ) return s

          // Remove rels for FK columns that were deleted
          let newRels = s.rels
          if (newCols !== undefined) {
            const oldFKNames = existing.cols
              .filter((c) => isForeignKey(c))
              .map((c) => c.name)
            const newFKSet = new Set(
              newCols.filter((c) => isForeignKey(c)).map((c) => c.name),
            )
            oldFKNames.forEach((colName) => {
              if (!newFKSet.has(colName)) {
                newRels = newRels.filter(
                  (r) => !(r.from === id && r.col === colName),
                )
              }
            })

            newRels = newRels.map((rel) => {
              if (rel.from !== id) return rel
              const fkColumn = newCols?.find((column) => column.name === rel.col)
              if (!fkColumn) return rel
              const cardinalities = getRelationshipCardinalities(rel)
              const toCardinality = fkColumn.notNull ? 'one' : 'zero-one'
              return {
                ...rel,
                toCardinality,
                type: relationTypeFromCardinalities(cardinalities.from, toCardinality),
              }
            })
          }

          const updated: Table = {
            ...existing,
            ...changes,
            ...(newCols !== undefined ? { cols: newCols } : {}),
          }

          const newTables = [...s.tables]
          newTables[idx] = updated
          return { tables: newTables, rels: newRels }
        })
        recordHistory(get(), 'update-table')
      },

      deleteTable: (id) => {
        set((s) => {
          return removeTablesBatch(s.tables, s.rels, [id])
        })
        recordHistory(get(), 'delete-table')
      },

      deleteTables: (ids) => {
        if (ids.length === 0) return
        set((s) => removeTablesBatch(s.tables, s.rels, ids))
        recordHistory(get(), 'delete-tables')
      },

      duplicateTable: (id) => {
        set((s) => {
          const src = s.tables.find((t) => t.id === id)
          if (!src) return s

          const existingFullNames = new Set(s.tables.map((table) => table.fullName))
          const nextIdentity = buildUniqueTableCopyIdentity(existingFullNames, src.name, src.schema)

          const clone = JSON.parse(JSON.stringify(src)) as Table
          const dup: Table = normTable({
            ...clone,
            id: newUid(),
            name: nextIdentity.name,
            schema: nextIdentity.schema,
            fullName: nextIdentity.fullName,
            // A cópia tem colunas distintas → novos ids estáveis.
            cols: clone.cols.map((c) => ({ ...c, id: newUid() })),
            x: src.x + 32,
            y: src.y + 32,
          })

          return { tables: [...s.tables, dup] }
        })
        recordHistory(get(), 'duplicate-table')
      },

      renameTable: (id, name, schema = '') => {
        const trimmedName = name.trim()
        const trimmedSchema = schema.trim()
        if (
          !isValidSqlIdentifier(trimmedName) ||
          (trimmedSchema !== '' && !isValidSqlIdentifier(trimmedSchema))
        ) return

        set((s) => {
          const idx = s.tables.findIndex((t) => t.id === id)
          if (idx < 0) return s

          const newFullName = buildFullTableName(trimmedSchema, trimmedName)
          if (!newFullName) return s

          const tbl = s.tables[idx]
          if (newFullName === tbl.fullName && trimmedSchema === tbl.schema && trimmedName === tbl.name) return s

          // id é UUID e NÃO muda → rels (from/to) intactos. Só atualizamos os
          // campos denormalizados de exibição que referenciam o NOME da tabela.
          const newRels = s.rels.map((r) => ({
            ...r,
            fromTableName: r.from === id ? newFullName : r.fromTableName,
            toTableName: r.to === id ? newFullName : r.toTableName,
            fromSchema: r.from === id ? trimmedSchema : r.fromSchema,
            toSchema: r.to === id ? trimmedSchema : r.toSchema,
          }))

          const updated: Table = {
            ...tbl,
            schema: trimmedSchema,
            name: trimmedName,
            fullName: newFullName,
          }

          const newTables = [...s.tables]
          newTables[idx] = updated
          return { tables: newTables, rels: newRels }
        })
        recordHistory(get(), 'rename-table')
      },

      // ────────────────────────────────────────────────────────────────────────
      // RELATION CRUD
      // ────────────────────────────────────────────────────────────────────────

      createRelationship: (srcId, tgtId, type) => {
        set((s) => {
          const src = s.tables.find((t) => t.id === srcId)
          const tgt = s.tables.find((t) => t.id === tgtId)
          if (!src || !tgt) return s

          const suffixNames = new Set(s.defaults.suffix.map((c) => c.name))

          const nullable = type === '0-1' || type === '0-N'

          // Helper: does this exact rel already exist?
          const relExists = (fromId: string, col: string, toId: string) =>
            s.rels.some(
              (r) => r.from === fromId && r.col === col && r.to === toId,
            )

          let newTables = [...s.tables]
          const newRels = [...s.rels]

          if (type === 'N-N') {
            // ── N-N: create junction table with two FKs ──────────────────────
            // id da junction é UUID; NOME (fullName) derivado dos nomes das tabelas.
            // Dedup pela junction existente do MESMO par (casada por fullName).
            const isSelfRelationship = srcId === tgtId
            const jBaseName = isSelfRelationship ? `${src.name}_self` : `${src.name}_${tgt.name}`
            const jFullName = buildFullTableName('', jBaseName) || jBaseName
            const fkS = isSelfRelationship ? `id_${src.name}_source` : `id_${src.name}`
            const fkT = isSelfRelationship ? `id_${tgt.name}_target` : `id_${tgt.name}`

            const existingJunction = s.tables.find((t) => t.fullName === jFullName)
            const jId = existingJunction?.id ?? newUid()

            if (!existingJunction) {
              const jx = Math.round((
                src.x + getTableWidth(src) / 2 +
                tgt.x + getTableWidth(tgt) / 2
              ) / 2 - TW / 2)
              const jy = Math.round(
                Math.max(
                  src.y + getTableHeight(src),
                  tgt.y + getTableHeight(tgt),
                ) + 80,
              )

              const junctionCols = enforceSinglePK([
                ...s.defaults.prefix.map((c) => normCol(c)),
                normCol({
                  key: 'FK',
                  name: fkS,
                  type: getForeignKeyType(src),
                  notNull: true,
                  blank: false,
                  desc: isSelfRelationship ? `FK ${src.name} (source)` : `FK ${src.name}`,
                }),
                normCol({
                  key: 'FK',
                  name: fkT,
                  type: getForeignKeyType(tgt),
                  notNull: true,
                  blank: false,
                  desc: isSelfRelationship ? `FK ${tgt.name} (target)` : `FK ${tgt.name}`,
                }),
                ...s.defaults.suffix.map((c) => normCol(c)),
              ])

              const junction = normTable({
                id: jId,
                name: jBaseName,
                schema: '',
                fullName: jFullName,
                g: Object.keys(s.groups)[0] || '',
                x: jx,
                y: jy,
                cols: junctionCols,
                indexes: [],
              })

              newTables = [...newTables, junction]
            }

            if (!relExists(jId, fkS, srcId)) {
              newRels.push(
                normRel({
                  from: jId,
                  col: fkS,
                  to: srcId,
                  type: '1-N',
                  mid: buildParallelRelationshipMid(newRels, jId, srcId),
                }),
              )
            }
            if (!relExists(jId, fkT, tgtId)) {
              newRels.push(
                normRel({
                  from: jId,
                  col: fkT,
                  to: tgtId,
                  type: '1-N',
                  mid: buildParallelRelationshipMid(newRels, jId, tgtId),
                }),
              )
            }
          } else {
            // ── 1-1, 0-1, 1-N, 0-N: FK goes to the "many/tgt" side ──────────
            // Update tgt's cols with the new FK column
            const tgtIdx = newTables.findIndex((t) => t.id === tgtId)
            if (tgtIdx >= 0) {
              const fk = buildNextFkColumnName(newTables[tgtIdx].cols, `id_${src.name}`)
              newTables = [...newTables]
              newTables[tgtIdx] = {
                ...newTables[tgtIdx],
                cols: insertFKCol(
                  newTables[tgtIdx].cols,
                  fk,
                  src.name,
                  nullable,
                  suffixNames,
                  getForeignKeyType(src),
                ),
              }
              newRels.push(
                normRel({
                  from: tgtId,
                  col: fk,
                  to: srcId,
                  type,
                  mid: buildParallelRelationshipMid(newRels, tgtId, srcId),
                }),
              )
            }
          }

          const existingRelationIds = new Set(s.rels.map((relation) => relation.id))
          const pinnedRels = newRels.map((relation) =>
            existingRelationIds.has(relation.id)
              ? relation
              : pinRelationshipRoute(relation, newTables),
          )

          return { tables: newTables, rels: pinnedRels }
        })
        recordHistory(get(), 'create-relationship')
      },

      deleteRelationship: (relId) => {
        set((s) => {
          const rel = s.rels.find((r) => r.id === relId)
          if (!rel) return s

          let newTables = s.tables

          // Remove FK column from rel.from table only if no other rel uses it
          if (!fkColStillUsed(s.rels, rel.from, rel.col, relId)) {
            newTables = s.tables.map((t) => {
              if (t.id !== rel.from) return t
              return { ...t, cols: t.cols.filter((c) => c.name !== rel.col) }
            })
          }

          return {
            tables: newTables,
            rels: s.rels.filter((r) => r.id !== relId),
          }
        })
        recordHistory(get(), 'delete-relationship')
      },

      updateRelationship: (relId, changes) => {
        set((s) => ({
          rels: s.rels.map((r) =>
            r.id === relId ? { ...r, ...changes } : r,
          ),
        }))
        recordHistory(get(), 'update-relationship')
      },

      invertRelationship: (relId) => {
        let createdRelation: Relation | null = null

        set((s) => {
          const rel = s.rels.find((entry) => entry.id === relId)
          if (!rel || rel.type === 'N-N') return s

          const src = s.tables.find((table) => table.id === rel.from)
          const tgt = s.tables.find((table) => table.id === rel.to)
          if (!src || !tgt) return s

          const suffixNames = new Set(s.defaults.suffix.map((c) => c.name))
          const nullable = rel.type === '0-1' || rel.type === '0-N'
          const nextFkName = buildNextFkColumnName(tgt.cols, `id_${src.name}`)

          let nextTables = s.tables.map((table) => {
            if (table.id === rel.from && !fkColStillUsed(s.rels, rel.from, rel.col, rel.id)) {
              return { ...table, cols: table.cols.filter((column) => column.name !== rel.col) }
            }

            if (table.id === rel.to) {
              return {
                ...table,
                cols: insertFKCol(
                  table.cols,
                  nextFkName,
                  rel.from,
                  nullable,
                  suffixNames,
                  getForeignKeyType(src),
                ),
              }
            }

            return table
          })

          const remainingRels = s.rels.filter((entry) => entry.id !== rel.id)

          createdRelation = pinRelationshipRoute(normRel({
            from: rel.to,
            col: nextFkName,
            to: rel.from,
            type: rel.type,
            mid: buildParallelRelationshipMid(remainingRels, rel.to, rel.from),
            pts: [...rel.pts],
            fromAnchor: rel.toAnchor ? { ...rel.toAnchor } : null,
            toAnchor: rel.fromAnchor ? { ...rel.fromAnchor } : null,
            fromTableName: rel.toTableName || tgt.name,
            toTableName: rel.fromTableName || src.name,
            fromSchema: rel.toSchema || tgt.schema,
            toSchema: rel.fromSchema || src.schema,
            fromColumn: nextFkName,
            toColumn: rel.toColumn || 'id',
            waypoints: rel.waypoints ? [...rel.waypoints].reverse().map((point) => ({ ...point })) : undefined,
            isManual: rel.isManual,
          }), nextTables)

          return {
            tables: nextTables,
            rels: [...remainingRels, createdRelation],
          }
        })

        if (createdRelation) {
          recordHistory(get(), 'invert-relationship')
        }

        return createdRelation
      },

      // ────────────────────────────────────────────────────────────────────────
      // GROUP CRUD
      // ────────────────────────────────────────────────────────────────────────

      addGroup: (group) => {
        set((s) => {
          const norm = normGroup(group)
          // Ensure unique key (use id from normGroup, fallback to genId)
          let key = norm.id || genId('grp')
          if (s.groups[key]) {
            key = key + '_' + genId('').slice(0, 5)
          }
          const final: Group = { ...norm, id: key }
          return { groups: { ...s.groups, [key]: final } }
        })
        recordHistory(get(), 'add-group')
      },

      updateGroup: (id, changes) => {
        set((s) => {
          if (!s.groups[id]) return s
          return {
            groups: {
              ...s.groups,
              [id]: normGroup({ ...s.groups[id], ...changes }),
            },
          }
        })
        recordHistory(get(), 'update-group')
      },

      deleteGroup: (id) => {
        set((s) => {
          const { [id]: removedGroup, ...rest } = s.groups
          void removedGroup
          return {
            groups: rest,
            tables: s.tables.map((t) => (t.g === id ? { ...t, g: '' } : t)),
          }
        })
        recordHistory(get(), 'delete-group')
      },

      // ────────────────────────────────────────────────────────────────────────
      // BULK / COMPAT
      // ────────────────────────────────────────────────────────────────────────

      pasteTablesAndRels: (newTables: Table[], newRels: Relation[]) => {
        set((s) => {
          const tables = [...s.tables, ...newTables.map(normTable)]
          const rels = pinAutomaticRelationshipRoutes(newRels.map(normRel), tables)
          return {
            tables,
            rels: [...s.rels, ...rels],
          }
        })
        recordHistory(get(), 'paste')
      },

      autoLayout: () => {
        const s = get()
        recordHistory(s, 'pre-auto-layout')
        const positioned = calculateImportedTablesLayout({
          currentTables: [],
          importedTables: s.tables,
          relationships: s.rels,
        })
        set({ tables: positioned })
        recordHistory(get(), 'auto-layout')
      },

      setTables: (tables) => set({ tables }),
      setRelations: (rels) => set((state) => ({
        rels: pinAutomaticRelationshipRoutes(rels.map(normRel), state.tables),
      })),
      setGroups: (groups) => set({ groups }),
      setDefaults: (defaults) => {
        const columns = [...defaults.prefix, ...defaults.suffix]
        if (columns.some((column) => !isValidSqlIdentifier(column.name))) return
        set({ defaults })
      },
      setProjectName: (name) => set({ projectName: name }),
      setDialect: (dialect) => set({ dialect }),

      loadDiagram: ({ tables, rels, groups, defaults, projectName, dialect }) =>
        set(() => {
          const normalizedTables = tables.map((table) => normTable(table))
          return {
            tables: normalizedTables,
            rels: pinAutomaticRelationshipRoutes(rels.map(normRel), normalizedTables),
            groups: normGroups(groups),
            defaults: {
              prefix: (defaults?.prefix ?? INIT_DEFAULTS.prefix).map(normCol),
              suffix: (defaults?.suffix ?? INIT_DEFAULTS.suffix).map(normCol),
            },
            ...(projectName !== undefined ? { projectName } : {}),
            dialect: dialect ?? 'postgresql',
          }
        }),
    }),
    {
      name: 'erd_editor_v5',
      storage: diagramStorage,
      partialize: (s) => ({
        tables: s.tables,
        rels: s.rels,
        groups: s.groups,
        defaults: s.defaults,
        projectName: s.projectName,
        dialect: s.dialect,
      }),
      // Migrate persisted data: normalise all entities on rehydration
      onRehydrateStorage: () => (state) => {
        if (!state) return
        // Migração de identidade do diagrama local (id=fullName → UUID). Remapeia
        // rels.from/to em conjunto. Idempotente para dados já em UUID.
        const migrated = migrateDiagramIdentity({
          tables: state.tables || [],
          rels: state.rels || [],
        })
        state.tables = migrated.tables.map((t) => normTable(t))
        state.rels = pinAutomaticRelationshipRoutes(
          migrated.rels.map(normRel),
          state.tables,
        )
        state.groups = normGroups(state.groups)
        state.defaults = {
          prefix: (state.defaults?.prefix ?? INIT_DEFAULTS.prefix).map(normCol),
          suffix: (state.defaults?.suffix ?? INIT_DEFAULTS.suffix).map(normCol),
        }
        // Legacy projects without dialect default to postgresql (RF-D05)
        if (!state.dialect) state.dialect = 'postgresql'
      },
    },
  ),
)

// ── Convenience selector helpers (use in components) ──────────────────────────

export const selectTableById = (id: string) => (s: DiagramState) =>
  s.tables.find((t) => t.id === id)

export const selectRelsForTable = (id: string) => (s: DiagramState) =>
  s.rels.filter((r) => r.from === id || r.to === id)

export const selectFKColsForTable = (id: string) => (s: DiagramState) => {
  const tbl = s.tables.find((t) => t.id === id)
  return tbl ? tbl.cols.filter((c) => isForeignKey(c)) : []
}

// Re-export legacy action aliases for code that still uses the old names
// (avoids search-and-replace across components during the incremental migration)
export const diagramActions = {
  addRelation: (rel: Partial<Relation>) =>
    useDiagramStore.getState().rels.find(
      (r) => r.from === rel.from && r.col === rel.col && r.to === rel.to,
    ) === undefined &&
    useDiagramStore.setState((s) => ({
      rels: [...s.rels, pinRelationshipRoute(normRel(rel), s.tables)],
    })),
  deleteRelation: (id: string) =>
    useDiagramStore.getState().deleteRelationship(id),
}
